import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReplayIcon from '@mui/icons-material/Replay';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { apiService, Schema } from '../../services/api';
import { prettyPrint } from '../../utils/dataUtils';
import type { DiffResult, ToastState } from './types';

interface DiffValidationCardProps {
  schemaId: string;
  diffSchemaId: string;
  setDiffSchemaId: (value: string) => void;
  schemaSetTableNames: string[];
  selectedSchemaSetName: string | undefined;
  selectedSchemaSetStatus: string | undefined;
  isSchemaLoading: boolean;
  isSchemaError: boolean;
  setToast: (value: ToastState | null) => void;
}

const DiffValidationCard: React.FC<DiffValidationCardProps> = ({
  schemaId,
  diffSchemaId,
  setDiffSchemaId,
  schemaSetTableNames,
  selectedSchemaSetName,
  selectedSchemaSetStatus,
  isSchemaLoading,
  isSchemaError,
  setToast,
}) => {
  const [diffEnvironment, setDiffEnvironment] = useState<string>('default');
  const [diffResult, setDiffResult] = useState<DiffResult | null>(null);
  const [diffLoading, setDiffLoading] = useState<boolean>(false);

  useEffect(() => {
    setDiffResult(null);
    setDiffSchemaId('');
  }, [diffSchemaId]);

  const diffSummary = useMemo(() => {
    if (!diffResult) {
      return null;
    }
    const summary = diffResult.summary ?? {};
    const tablesAdded = Array.isArray(diffResult.tables_added) ? diffResult.tables_added.length : 0;
    const tablesRemoved = Array.isArray(diffResult.tables_removed) ? diffResult.tables_removed.length : 0;
    const tablesChanged = Array.isArray(diffResult.tables_changed) ? diffResult.tables_changed.length : 0;
    return { ...summary, tablesAdded, tablesRemoved, tablesChanged };
  }, [diffResult]);

  const handleRequestDiff = useCallback(async () => {
    if (!schemaId) {
      return;
    }
    setDiffLoading(true);
    setDiffResult(null);
    try {
      const response = await apiService.diffSchema(schemaId, diffSchemaId);
      setDiffResult(response as DiffResult);
    } catch (error) {
      console.error('Failed to compute diff:', error);
      setToast({
        severity: 'error',
        message: 'Unable to compute diff. Ensure a schema/version is available.',
      });
    } finally {
      setDiffLoading(false);
    }
  }, [schemaId, diffSchemaId, diffEnvironment, setToast]);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Diff & Validation
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Schema ID"
            value={diffSchemaId}
            onChange={(event) => setDiffSchemaId(event.target.value)}
            size="small"
            helperText={
              isSchemaLoading
                ? 'Loading schema metadata\u2026'
                : selectedSchemaSetName
                  ? `Loaded: ${selectedSchemaSetName}${selectedSchemaSetStatus ? ` (${selectedSchemaSetStatus})` : ''}`
                  : 'Provide a schema ID to compare against.'
            }
          />
          {isSchemaError && (
            <Alert severity="warning">
              Unable to load schema. Verify the ID or try again later.
            </Alert>
          )}
          {isSchemaLoading && !isSchemaError && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={18} />
              <Typography variant="caption" color="text.secondary">
                Fetching schema details\u2026
              </Typography>
            </Box>
          )}
          {schemaSetTableNames.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Target schema contains {schemaSetTableNames.length} table{schemaSetTableNames.length !== 1 ? 's' : ''}.
            </Typography>
          )}
          <TextField
            label="Environment"
            value={diffEnvironment}
            onChange={(event) => setDiffEnvironment(event.target.value)}
            size="small"
            helperText="Used when diffing against the active schema set (leave as default otherwise)."
          />
          <Button
            variant="outlined"
            startIcon={<ReplayIcon />}
            onClick={handleRequestDiff}
            disabled={diffLoading || !diffSchemaId}
          >
            {diffLoading ? 'Computing Diff\u2026' : 'Run Diff'}
          </Button>

          {diffSummary && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Diff Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Tables Added
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {diffSummary.tablesAdded}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Tables Removed
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {diffSummary.tablesRemoved}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Tables Changed
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {diffSummary.tablesChanged}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {diffResult && (
            <>
              {Array.isArray(diffResult.tables_added) && diffResult.tables_added.length > 0 && (
                <Accordion disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      Tables Added ({diffResult.tables_added.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense disablePadding>
                      {diffResult.tables_added.map((table) => (
                        <ListItem key={`table-added-${table}`} disablePadding>
                          <ListItemText primary={table} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}

              {Array.isArray(diffResult.tables_removed) && diffResult.tables_removed.length > 0 && (
                <Accordion disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      Tables Removed ({diffResult.tables_removed.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense disablePadding>
                      {diffResult.tables_removed.map((table) => (
                        <ListItem key={`table-removed-${table}`} disablePadding>
                          <ListItemText primary={table} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}

              {Array.isArray(diffResult.tables_changed) && diffResult.tables_changed.length > 0 && (
                <Accordion disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      Tables Changed ({diffResult.tables_changed.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {diffResult.tables_changed.map((change) => {
                        const columnAdds = Array.isArray(change.columns_added)
                          ? change.columns_added
                          : [];
                        const columnRemovals = Array.isArray(change.columns_removed)
                          ? change.columns_removed
                          : [];
                        const columnTypeChanges = Array.isArray(change.column_type_changes)
                          ? change.column_type_changes
                          : [];
                        return (
                          <Box key={`table-change-${change.table}`}>
                            <Typography variant="subtitle2">{change.table}</Typography>
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              {columnAdds.length > 0 && (
                                <Box>
                                  <Typography variant="caption" color="success.main">
                                    Columns Added ({columnAdds.length})
                                  </Typography>
                                  <List dense disablePadding sx={{ pl: 2 }}>
                                    {columnAdds.map((column) => (
                                      <ListItem
                                        key={`column-added-${change.table}-${column}`}
                                        disablePadding
                                      >
                                        <ListItemText primary={column} />
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}
                              {columnRemovals.length > 0 && (
                                <Box>
                                  <Typography variant="caption" color="error.main">
                                    Columns Removed ({columnRemovals.length})
                                  </Typography>
                                  <List dense disablePadding sx={{ pl: 2 }}>
                                    {columnRemovals.map((column) => (
                                      <ListItem
                                        key={`column-removed-${change.table}-${column}`}
                                        disablePadding
                                      >
                                        <ListItemText primary={column} />
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}
                              {columnTypeChanges.length > 0 && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Column Type Changes ({columnTypeChanges.length})
                                  </Typography>
                                  <List dense disablePadding sx={{ pl: 2 }}>
                                    {columnTypeChanges.map((columnChange, index) => {
                                      const fromType =
                                        columnChange.from ?? columnChange.from_type ?? 'unknown';
                                      const toType =
                                        columnChange.to ?? columnChange.to_type ?? 'unknown';
                                      const columnName = columnChange.column ?? 'Unnamed Column';
                                      return (
                                        <ListItem
                                          key={`column-type-change-${change.table}-${columnName}-${index}`}
                                          disablePadding
                                        >
                                          <ListItemText
                                            primary={columnName}
                                            secondary={`${fromType} \u2192 ${toType}`}
                                          />
                                        </ListItem>
                                      );
                                    })}
                                  </List>
                                </Box>
                              )}
                              {columnAdds.length === 0 &&
                                columnRemovals.length === 0 &&
                                columnTypeChanges.length === 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    Detected change without detailed column metadata.
                                  </Typography>
                                )}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Diff Payload
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<SaveAltIcon fontSize="small" />}
                    component="a"
                    href={`data:application/json;charset=utf-8,${encodeURIComponent(
                      JSON.stringify(diffResult, null, 2)
                    )}`}
                    download={`schema-diff-${schemaId}.json`}
                  >
                    Download Diff
                  </Button>
                </Stack>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 260, overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: 12 }}>{prettyPrint(diffResult)}</pre>
                </Paper>
              </Box>
            </>
          )}
          {!diffResult && !diffLoading && (
            <Typography variant="caption" color="text.secondary">
              Provide a schema or specific schema version to compare and run the diff.
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DiffValidationCard;
